// ============================================================================
// SKILL LEVEL CONTEXT - PRODUCTION v2.1 (BUG FIXES)
// ============================================================================
// FIXES APPLIED:
// 1. canClaimDaily now checks `completed && !claimed` (not `current >= target && !completed`)
// 2. canClaimWeekly same fix
// ============================================================================

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from './AuthContext';
import geoService from '../services/geoService'; //  For geo restriction checks


// ============================================================================
// TYPES
// ============================================================================

export type SkillLevel = 'beginner' | 'intermediate' | 'advanced' | 'pro';

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  earnedAt: number;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  pkCashReward?: number;
}

export interface DailyChallenge {
  target: number;
  current: number;
  completed: boolean;
  claimed?: boolean;
  // Rotating challenge fields
  name?: string;
  description?: string;
  pkCash?: number;
}

export interface DailyRewards {
  lastClaimDate: string;
  streakDays: number;
  todaysChallenges: {
    playHands: DailyChallenge;
    completeScenarios: DailyChallenge;
    getCorrect: DailyChallenge;
  };
  streakBonusClaimed?: boolean;
  allChallengesCompletedOn?: string | null;
}

export interface WeeklyChallenge {
  id: string;
  name: string;
  description: string;
  target: number;
  current: number;
  completed: boolean;
  claimed?: boolean;
  pkCashReward: number;
  type?: string;
}

export interface WeeklyRewards {
  weekStart: string;
  challenges: WeeklyChallenge[];
  totalPKCashAvailable: number;
  claimed: boolean;
}

export interface SkillProgress {
  level: SkillLevel;
  xp: number;
  xpToNextLevel: number;
  totalHandsPlayed: number;
  scenariosCompleted: number;
  correctAnswers: number;
  streakDays: number;
  lastPlayedAt: number;
  lastRewardClaimedAt: number;
  badges: Badge[];
  unlockedConcepts: string[];
  pkCashEarned: number;
  playthroughFromTraining: number;
  dailyRewardsClaimed: DailyRewards;
  weeklyChallenge: WeeklyRewards | null;
}

export interface LevelConfig {
  name: string;
  displayName: string;
  icon: string;
  color: string;
  description: string;
  xpRequired: number;
  features: string[];
  concepts: string[];
  explanationStyle: 'simple' | 'detailed' | 'technical' | 'advanced';
}

export interface ActivityResult {
  success: boolean;
  xpEarned: number;
  pkCashEarned: number;
  playthroughAdded: number;
  newXP: number;
  newLevel: SkillLevel;
  leveledUp: boolean;
  newBadges: Badge[];
  rewards: string[];
  error?: string;
}

export interface ClaimResult {
  success: boolean;
  pkCashEarned: number;
  playthroughAdded: number;
  rewards: string[];
  streakDays?: number;
  error?: string;
}

// ============================================================================
// LEVEL CONFIGURATIONS
// ============================================================================

export const LEVEL_CONFIGS: Record<SkillLevel, LevelConfig> = {
  beginner: {
    name: 'beginner',
    displayName: 'Rookie',
    icon: '',

    color: '#4CAF50',
    description: 'Learning the basics of poker',
    xpRequired: 0,
    features: [
      'Basic hand rankings',
      'Position fundamentals',
      'When to fold/call/raise',
      'Simple pot odds',
    ],
    concepts: [
      'hand-rankings',
      'position-basics',
      'preflop-fundamentals',
      'basic-betting',
    ],
    explanationStyle: 'simple',
  },
  intermediate: {
    name: 'intermediate',
    displayName: 'Grinder',
    icon: '',

    color: '#2196F3',
    description: 'Building solid fundamentals',
    xpRequired: 3000,
    features: [
      'Pot odds & equity',
      'Continuation betting',
      'Board texture reading',
      'Semi-bluffing',
      'Value betting',
    ],
    concepts: [
      'pot-odds',
      'equity-calculation',
      'c-betting',
      'board-texture',
      'semi-bluffing',
      'value-betting',
    ],
    explanationStyle: 'detailed',
  },
  advanced: {
    name: 'advanced',
    displayName: 'Shark',
    icon: '',

    color: '#9C27B0',
    description: 'Mastering advanced concepts',
    xpRequired: 15000,
    features: [
      'Range analysis',
      'Implied odds',
      'Bet sizing optimization',
      'Exploitative play',
      'Multi-street planning',
    ],
    concepts: [
      'range-thinking',
      'implied-odds',
      'bet-sizing',
      'exploitative-play',
      'thin-value',
      'polarization',
    ],
    explanationStyle: 'technical',
  },
  pro: {
    name: 'pro',
    displayName: 'Pro',
    icon: '',

    color: '#FF9800',
    description: 'Tournament-ready strategy',
    xpRequired: 50000,
    features: [
      'GTO fundamentals',
      'MDF calculations',
      'Solver-based strategies',
      'ICM considerations',
      'Advanced exploitation',
    ],
    concepts: [
      'gto-basics',
      'mdf',
      'solver-lines',
      'icm',
      'advanced-exploitation',
      'balance',
    ],
    explanationStyle: 'advanced',
  },
};

// ============================================================================
// BADGE DEFINITIONS (for display)
// ============================================================================

export const BADGE_DEFINITIONS: Record<string, Omit<Badge, 'earnedAt'>> = {
  'first-hand': { id: 'first-hand', name: 'First Steps', description: 'Play your first training hand', icon: '', tier: 'bronze', pkCashReward: 0.5 },

  'ten-hands': { id: 'ten-hands', name: 'Getting Started', description: 'Play 10 training hands', icon: '🃏', tier: 'bronze', pkCashReward: 1 },
  'first-scenario': { id: 'first-scenario', name: 'Student', description: 'Complete your first scenario', icon: '', tier: 'bronze', pkCashReward: 0.5 },

  'preflop-master': { id: 'preflop-master', name: 'Preflop Pro', description: 'Get 10 preflop scenarios correct', icon: '', tier: 'silver', pkCashReward: 10 },

  'pot-odds-wizard': { id: 'pot-odds-wizard', name: 'Pot Odds Wizard', description: 'Get 10 pot odds scenarios correct', icon: '', tier: 'silver', pkCashReward: 10 },

  'hundred-hands': { id: 'hundred-hands', name: 'Centurion', description: 'Play 100 training hands', icon: '', tier: 'silver', pkCashReward: 3 },

  'week-streak': { id: 'week-streak', name: 'Dedicated', description: 'Practice 7 days in a row', icon: '', tier: 'silver', pkCashReward: 20 },

  'five-hundred-hands': { id: 'five-hundred-hands', name: 'Grinder', description: 'Play 500 training hands', icon: '', tier: 'gold', pkCashReward: 8 },

  'bluff-master': { id: 'bluff-master', name: 'Bluff Master', description: 'Get 15 bluffing scenarios correct', icon: '', tier: 'gold', pkCashReward: 20 },

  'value-town': { id: 'value-town', name: 'Value Town Mayor', description: 'Get 15 value betting scenarios correct', icon: '', tier: 'gold', pkCashReward: 20 },

  'thousand-hands': { id: 'thousand-hands', name: 'Veteran', description: 'Play 1000 training hands', icon: '', tier: 'platinum', pkCashReward: 15 },

  'five-thousand-hands': { id: 'five-thousand-hands', name: 'Iron Grinder', description: 'Play 5,000 training hands', icon: '🔥', tier: 'platinum', pkCashReward: 25 },

  'ten-thousand-hands': { id: 'ten-thousand-hands', name: 'Legend', description: 'Play 10,000 training hands', icon: '👑', tier: 'platinum', pkCashReward: 40 },

  'all-concepts': { id: 'all-concepts', name: 'Complete Player', description: 'Master all training concepts', icon: '', tier: 'platinum', pkCashReward: 100 },

  'perfect-score': { id: 'perfect-score', name: 'Perfectionist', description: 'Get 250 scenario answers correct', icon: '⭐', tier: 'platinum', pkCashReward: 5 },
};

// ============================================================================
// CONTEXT INTERFACE
// ============================================================================

interface SkillLevelContextType {
  skillLevel: SkillLevel;
  progress: SkillProgress | null;
  levelConfig: LevelConfig;
  isLoading: boolean;
  error: string | null;
  
  dailyRewards: DailyRewards | null;
  weeklyRewards: WeeklyRewards | null;
  canClaimDaily: boolean;
  canClaimWeekly: boolean;
  
  refreshProgress: () => Promise<void>;
  setSkillLevel: (level: SkillLevel) => Promise<void>;
  recordHandPlayed: (count?: number) => Promise<ActivityResult>;
  recordScenarioCompleted: (correct: boolean, concept?: string) => Promise<ActivityResult>;
  recordSessionCompleted: (score: number) => Promise<ActivityResult>;
  claimDailyRewards: () => Promise<ClaimResult>;
  claimWeeklyRewards: () => Promise<ClaimResult>;
  
  getExplanationForLevel: (simple: string, detailed: string, technical: string, advanced: string) => string;
  isConceptUnlocked: (concept: string) => boolean;
  getProgressToNextLevel: () => { current: number; required: number; percentage: number };
  getDailyProgress: () => { completed: number; total: number; percentage: number };
  getWeeklyProgress: () => { completed: number; total: number; percentage: number; pkCashAvailable: number };
}

const SkillLevelContext = createContext<SkillLevelContextType | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

export const SkillLevelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [skillLevel, setSkillLevelState] = useState<SkillLevel>('beginner');
  const [progress, setProgress] = useState<SkillProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const functions = getFunctions();
  
  // Cloud function references
  const getProgressFn = httpsCallable(functions, 'getTrainingProgress');
  const recordActivityFn = httpsCallable(functions, 'recordTrainingActivity');
  const claimDailyFn = httpsCallable(functions, 'claimDailyRewards');
  const claimWeeklyFn = httpsCallable(functions, 'claimWeeklyRewards');
  const setLevelFn = httpsCallable(functions, 'setTrainingSkillLevel');

  // Load progress on mount
  useEffect(() => {
    if (currentUser?.uid) {
      refreshProgress();
    } else {
      setIsLoading(false);
      setProgress(null);
    }
  }, [currentUser?.uid]);

  // Refresh progress from server
  const refreshProgress = useCallback(async () => {
    if (!currentUser?.uid) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getProgressFn({});
      const data = result.data as { success: boolean; progress: SkillProgress; error?: string };
      
      if (data.success && data.progress) {
        setProgress(data.progress);
        setSkillLevelState(data.progress.level);
      } else {
        setError(data.error || 'Failed to load progress');
      }
    } catch (err: any) {
      console.error('Failed to load training progress:', err);
      setError(err.message || 'Failed to load progress');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.uid, getProgressFn]);

  // Set skill level (manual override)
  const setSkillLevel = useCallback(async (level: SkillLevel) => {
    if (!currentUser?.uid) return;
    
    try {
      const result = await setLevelFn({ level });
      const data = result.data as { success: boolean; level: SkillLevel; unlockedConcepts: string[] };
      
      if (data.success) {
        setSkillLevelState(data.level);
        if (progress) {
          setProgress({
            ...progress,
            level: data.level,
            unlockedConcepts: data.unlockedConcepts,
          });
        }
      }
    } catch (err: any) {
      console.error('Failed to set skill level:', err);
      throw err;
    }
  }, [currentUser?.uid, progress, setLevelFn]);

  // Record hand played (secure)
  const recordHandPlayed = useCallback(async (count: number = 1): Promise<ActivityResult> => {
    if (!currentUser?.uid) {
      return { success: false, xpEarned: 0, pkCashEarned: 0, playthroughAdded: 0, newXP: 0, newLevel: skillLevel, leveledUp: false, newBadges: [], rewards: [], error: 'Not logged in' };
    }
    
    try {
      //  Get stateCode for geo restriction check (PlayCoins vs PSS Coins)

      const geoResult = geoService.getCachedResult();
      const stateCode = geoResult?.region;
      
      const result = await recordActivityFn({ activityType: 'hand_played', count, stateCode });
      const data = result.data as ActivityResult;
      
      if (data.success) {
        // Update local state
        await refreshProgress();
      }
      
      return data;
    } catch (err: any) {
      console.error('Failed to record hand:', err);
      return { success: false, xpEarned: 0, pkCashEarned: 0, playthroughAdded: 0, newXP: progress?.xp || 0, newLevel: skillLevel, leveledUp: false, newBadges: [], rewards: [], error: err.message };
    }
  }, [currentUser?.uid, skillLevel, progress, recordActivityFn, refreshProgress]);

  // Record scenario completed (secure)
  const recordScenarioCompleted = useCallback(async (correct: boolean, concept?: string): Promise<ActivityResult> => {
    if (!currentUser?.uid) {
      return { success: false, xpEarned: 0, pkCashEarned: 0, playthroughAdded: 0, newXP: 0, newLevel: skillLevel, leveledUp: false, newBadges: [], rewards: [], error: 'Not logged in' };
    }
    
    try {
      //  Get stateCode for geo restriction check (PlayCoins vs PSS Coins)

      const geoResult = geoService.getCachedResult();
      const stateCode = geoResult?.region;
      
      // Record both completion and correctness
      await recordActivityFn({ activityType: 'scenario_completed', count: 1, concept, stateCode });
      
      if (correct) {
        const result = await recordActivityFn({ activityType: 'scenario_correct', count: 1, concept, stateCode });
        const data = result.data as ActivityResult;
        await refreshProgress();
        return data;
      }
      
      await refreshProgress();
      return { success: true, xpEarned: 10, pkCashEarned: 0, playthroughAdded: 0, newXP: (progress?.xp || 0) + 10, newLevel: skillLevel, leveledUp: false, newBadges: [], rewards: [] };
    } catch (err: any) {
      console.error('Failed to record scenario:', err);
      return { success: false, xpEarned: 0, pkCashEarned: 0, playthroughAdded: 0, newXP: progress?.xp || 0, newLevel: skillLevel, leveledUp: false, newBadges: [], rewards: [], error: err.message };
    }
  }, [currentUser?.uid, skillLevel, progress, recordActivityFn, refreshProgress]);

  // Record session completed (secure)
  const recordSessionCompleted = useCallback(async (score: number): Promise<ActivityResult> => {
    if (!currentUser?.uid) {
      return { success: false, xpEarned: 0, pkCashEarned: 0, playthroughAdded: 0, newXP: 0, newLevel: skillLevel, leveledUp: false, newBadges: [], rewards: [], error: 'Not logged in' };
    }
    
    try {
      //  Get stateCode for geo restriction check (PlayCoins vs PSS Coins)

      const geoResult = geoService.getCachedResult();
      const stateCode = geoResult?.region;
      
      const result = await recordActivityFn({ activityType: 'session_completed', score, stateCode });
      const data = result.data as ActivityResult;
      
      if (data.success) {
        await refreshProgress();
      }
      
      return data;
    } catch (err: any) {
      console.error('Failed to record session:', err);
      return { success: false, xpEarned: 0, pkCashEarned: 0, playthroughAdded: 0, newXP: progress?.xp || 0, newLevel: skillLevel, leveledUp: false, newBadges: [], rewards: [], error: err.message };
    }
  }, [currentUser?.uid, skillLevel, progress, recordActivityFn, refreshProgress]);

  // Claim daily rewards
  const claimDailyRewards = useCallback(async (): Promise<ClaimResult> => {
    if (!currentUser?.uid) {
      return { success: false, pkCashEarned: 0, playthroughAdded: 0, rewards: [], error: 'Not logged in' };
    }
    
    try {
      //  Get stateCode for geo restriction check (PlayCoins vs PSS Coins)

      const geoResult = geoService.getCachedResult();
      const stateCode = geoResult?.region;
      
      const result = await claimDailyFn({ stateCode });
      const data = result.data as ClaimResult;
      
      if (data.success) {
        await refreshProgress();
      }
      
      return data;
    } catch (err: any) {
      console.error('Failed to claim daily rewards:', err);
      return { success: false, pkCashEarned: 0, playthroughAdded: 0, rewards: [], error: err.message };
    }
  }, [currentUser?.uid, claimDailyFn, refreshProgress]);

  // Claim weekly rewards
  const claimWeeklyRewards = useCallback(async (): Promise<ClaimResult> => {
    if (!currentUser?.uid) {
      return { success: false, pkCashEarned: 0, playthroughAdded: 0, rewards: [], error: 'Not logged in' };
    }
    
    try {
      //  Get stateCode for geo restriction check (PlayCoins vs PSS Coins)

      const geoResult = geoService.getCachedResult();
      const stateCode = geoResult?.region;
      
      const result = await claimWeeklyFn({ stateCode });
      const data = result.data as ClaimResult;
      
      if (data.success) {
        await refreshProgress();
      }
      
      return data;
    } catch (err: any) {
      console.error('Failed to claim weekly rewards:', err);
      return { success: false, pkCashEarned: 0, playthroughAdded: 0, rewards: [], error: err.message };
    }
  }, [currentUser?.uid, claimWeeklyFn, refreshProgress]);

  // Helper: Get level-appropriate explanation
  const getExplanationForLevel = useCallback((
    simple: string,
    detailed: string,
    technical: string,
    advanced: string
  ): string => {
    const style = LEVEL_CONFIGS[skillLevel].explanationStyle;
    switch (style) {
      case 'simple': return simple;
      case 'detailed': return detailed;
      case 'technical': return technical;
      case 'advanced': return advanced;
      default: return detailed;
    }
  }, [skillLevel]);

  // Helper: Check if concept is unlocked
  const isConceptUnlocked = useCallback((concept: string): boolean => {
    if (!progress) return false;
    return progress.unlockedConcepts.includes(concept);
  }, [progress]);

  // Helper: Get XP progress to next level
  const getProgressToNextLevel = useCallback(() => {
    if (!progress) return { current: 0, required: 3000, percentage: 0 };
    
    const currentRequired = LEVEL_CONFIGS[progress.level].xpRequired;
    const nextRequired = progress.xpToNextLevel;
    const currentXPInLevel = progress.xp - currentRequired;
    const xpNeededForLevel = nextRequired - currentRequired;
    const percentage = xpNeededForLevel > 0 
      ? Math.min((currentXPInLevel / xpNeededForLevel) * 100, 100)
      : 100;
    
    return { current: currentXPInLevel, required: xpNeededForLevel, percentage };
  }, [progress]);

  // Helper: Get daily progress
  const getDailyProgress = useCallback(() => {
    if (!progress?.dailyRewardsClaimed) {
      return { completed: 0, total: 3, percentage: 0 };
    }
    
    const challenges = progress.dailyRewardsClaimed.todaysChallenges;
    let completed = 0;
    if (challenges.playHands.completed) completed++;
    if (challenges.completeScenarios.completed) completed++;
    if (challenges.getCorrect.completed) completed++;
    
    return { completed, total: 3, percentage: (completed / 3) * 100 };
  }, [progress]);

  // Helper: Get weekly progress
  const getWeeklyProgress = useCallback(() => {
    if (!progress?.weeklyChallenge) {
      return { completed: 0, total: 3, percentage: 0, pkCashAvailable: 0 };
    }
    
    const challenges = progress.weeklyChallenge.challenges;
    let completed = 0;
    let pkCashAvailable = 0;
    
    for (const c of challenges) {
      if (c.completed) {
        completed++;
        if (!c.claimed) pkCashAvailable += c.pkCashReward;
      }
    }
    
    return { completed, total: challenges.length, percentage: (completed / challenges.length) * 100, pkCashAvailable };
  }, [progress]);

  // ============================================================================
  //  CRITICAL FIX: canClaimDaily was checking wrong condition

  // ============================================================================
  // OLD (BROKEN): c.playHands.current >= c.playHands.target && !c.playHands.completed
  // NEW (FIXED):  c.playHands.completed && !c.playHands.claimed
  // ============================================================================
  const canClaimDaily = progress?.dailyRewardsClaimed ? (() => {
    const c = progress.dailyRewardsClaimed.todaysChallenges;
    return (
      (c.playHands.completed && !c.playHands.claimed) ||
      (c.completeScenarios.completed && !c.completeScenarios.claimed) ||
      (c.getCorrect.completed && !c.getCorrect.claimed)
    );
  })() : false;

  // ============================================================================
  //  CRITICAL FIX: canClaimWeekly same fix

  // ============================================================================
  const canClaimWeekly = progress?.weeklyChallenge ? (() => {
    return progress.weeklyChallenge.challenges.some(c => 
      c.completed && !c.claimed
    );
  })() : false;

  const value: SkillLevelContextType = {
    skillLevel,
    progress,
    levelConfig: LEVEL_CONFIGS[skillLevel],
    isLoading,
    error,
    dailyRewards: progress?.dailyRewardsClaimed || null,
    weeklyRewards: progress?.weeklyChallenge || null,
    canClaimDaily,
    canClaimWeekly,
    refreshProgress,
    setSkillLevel,
    recordHandPlayed,
    recordScenarioCompleted,
    recordSessionCompleted,
    claimDailyRewards,
    claimWeeklyRewards,
    getExplanationForLevel,
    isConceptUnlocked,
    getProgressToNextLevel,
    getDailyProgress,
    getWeeklyProgress,
  };

  return (
    <SkillLevelContext.Provider value={value}>
      {children}
    </SkillLevelContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

export function useSkillLevel() {
  const context = useContext(SkillLevelContext);
  if (!context) {
    throw new Error('useSkillLevel must be used within a SkillLevelProvider');
  }
  return context;
}

export default SkillLevelContext;